/* eslint-disable no-nested-ternary */
import React, { useMemo } from "react";

import { useTheme } from "@mui/material/styles";
import { colors } from "@zapalacorp/tokens";
import Skeleton from "Components/Generic/Skeleton";
import { useCustomerPortalJobContext } from "Context/CustomerPortalJobContext";
import { useTranslation } from "react-i18next";
import { animated, useSpring } from "react-spring";
import groupBy from "Util/groupBy";

import ChevronRight from "../../../../../../components/Generic/Icons/ChevronRight";
import { useCustomerPortalContext } from "../../../../../../context/CustomerPortalContext";
import { CustomerPortalMilestoneStep } from "../../../../../../models/customer.portal";
import {
  CustomerPortalMilestoneStepStatus,
  MilestonesDefaultTasksTitles,
  TaskType,
} from "../../../../../../util/enums";
import useStyles, {
  ButtonAllSteps,
  ContainerInfoButton,
  HeaderStepper,
  MoveProgressLabel,
  OpenMilestonesButtonWrapper,
  ProgressBar,
  StepContainer,
  StepLineChild,
  StepLineChildFilled,
  StepLineContainer,
  StepLineInProgress,
  StepLineLabel,
} from "./styled";

const fixedStepsValues = [TaskType.PACK, TaskType.UPLIFT, "Shipment", TaskType.DELIVERY];

enum StepProgress {
  PLANNED = "PLANNED",
  PROGRESS = "PROGRESS",
  FINISHED = "FINISHED",
}

type MilestoneStep = {
  type: typeof fixedStepsValues[0];
  progress: StepProgress;
};

const fixedStepsValuesDefault: Array<MilestoneStep> = [
  {
    type: fixedStepsValues[0],
    progress: StepProgress.PLANNED,
  },
  {
    type: fixedStepsValues[1],
    progress: StepProgress.PLANNED,
  },
  {
    type: fixedStepsValues[2],
    progress: StepProgress.PLANNED,
  },
  {
    type: fixedStepsValues[3],
    progress: StepProgress.PLANNED,
  },
];

type Props = {
  onOpenAllSteps?: () => void;
  isMobileDevice?: boolean;
  milestonesSteps?: CustomerPortalMilestoneStep[];
};

const renderMilestoneStepStatus = (
  milestonesStepStatus: CustomerPortalMilestoneStepStatus,
  rightPosition?: string | number
) => {
  switch (milestonesStepStatus) {
    case CustomerPortalMilestoneStepStatus.COMPLETED:
      return <StepLineChildFilled right={rightPosition} />;

    case CustomerPortalMilestoneStepStatus.IN_PROGRESS:
      return <StepLineInProgress right={rightPosition} />;

    case CustomerPortalMilestoneStepStatus.PENDING:
    case CustomerPortalMilestoneStepStatus.CANCELLED:
    case CustomerPortalMilestoneStepStatus.REJECTED:
    default:
      return <StepLineChild right={rightPosition} />;
  }
};

const StepLineComponent: React.FC<Props> = ({
  onOpenAllSteps,
  isMobileDevice,
  milestonesSteps,
}) => {
  const { t: translate } = useTranslation();
  const { isMileStonesOpenedInMobile, handleOpenMileStonesInMobile } = useCustomerPortalContext();
  const { isLoading, isMilestonesStepsLoading } = useCustomerPortalJobContext();
  const classes = useStyles();

  const getLabelStatus = (value: string) => {
    switch (value.toLowerCase()) {
      case fixedStepsValues[0].toLowerCase():
        return translate("packed");
      case fixedStepsValues[1].toLowerCase():
        return translate("loaded");
      case fixedStepsValues[2].toLowerCase():
        return translate("transport");
      case fixedStepsValues[3].toLowerCase():
        return translate("delivery");
      default:
        return "";
    }
  };

  const toggleWrapperAnimatedStyle = useSpring({
    transform: isMileStonesOpenedInMobile ? "rotate(180deg)" : "rotate(0deg)",
  });

  const actualSteps = useMemo(() => {
    const stepTask = (milestonesSteps || []).find(
      (step) => step.action === MilestonesDefaultTasksTitles.tasks
    );

    if (!stepTask || !stepTask.steps || !stepTask.steps.length) {
      return fixedStepsValuesDefault;
    }

    const groupped = groupBy(stepTask.steps, "action" as keyof typeof stepTask);

    if (!groupped || !Object.values(groupped).length) return fixedStepsValuesDefault;

    const mainSteps = [...fixedStepsValuesDefault];

    fixedStepsValues.forEach((validStepName, index) => {
      const steps =
        groupped[
          Object.keys(groupped).find((key) =>
            key.toLowerCase()?.includes(validStepName.toLowerCase())
          ) || ""
        ];

      if (steps && Array.isArray(steps) && steps.length) {
        const stepHasTaskCompleted = steps.some(
          (step) => step.status === CustomerPortalMilestoneStepStatus.COMPLETED
        );
        const stepHasTaskInProgress = steps.some(
          (step) => step.status === CustomerPortalMilestoneStepStatus.IN_PROGRESS
        );

        mainSteps[index] = {
          ...mainSteps[index],
          progress: stepHasTaskCompleted
            ? StepProgress.FINISHED
            : stepHasTaskInProgress
            ? StepProgress.PROGRESS
            : mainSteps[index].progress,
        };
      }
    });

    if (!mainSteps || !mainSteps.length) return fixedStepsValuesDefault;

    // const lastIndexCompleted = mainSteps
    //   .map((step) => step.progress === StepProgress.FINISHED)
    //   .lastIndexOf(true);

    // if (lastIndexCompleted > -1 && mainSteps[lastIndexCompleted + 1]) {
    //   mainSteps[lastIndexCompleted + 1] = {
    //     ...mainSteps[lastIndexCompleted + 1],
    //     progress: StepProgress.PROGRESS,
    //   };
    // }

    return mainSteps;
  }, [milestonesSteps]);

  const theme = useTheme();

  return (
    <Skeleton
      isLoading={isLoading || isMilestonesStepsLoading}
      classesName={classes.milestonesLoading}
    >
      <HeaderStepper>
        <ContainerInfoButton>
          {isMobileDevice ? (
            <ButtonAllSteps onClick={handleOpenMileStonesInMobile}>
              <animated.div style={toggleWrapperAnimatedStyle}>
                <ChevronRight color={colors.gray600} height={24} width={24} />
              </animated.div>
            </ButtonAllSteps>
          ) : (
            <OpenMilestonesButtonWrapper>
              <MoveProgressLabel>{translate("yourMoveProgress")}</MoveProgressLabel>
              <ButtonAllSteps onClick={onOpenAllSteps}>
                <span>{translate("showMore")}</span>
                <ChevronRight
                  color={theme.palette.primary.main}
                  height={16}
                  width={16}
                  heightBoxView={20}
                  widthBoxView={20}
                />
              </ButtonAllSteps>
            </OpenMilestonesButtonWrapper>
          )}
        </ContainerInfoButton>
        <ProgressBar>
          {actualSteps.map((step) => {
            return (
              <StepContainer key={`${step.type}`}>
                <StepLineContainer>
                  {renderMilestoneStepStatus(
                    step.progress === StepProgress.FINISHED
                      ? CustomerPortalMilestoneStepStatus.COMPLETED
                      : step.progress === StepProgress.PROGRESS
                      ? CustomerPortalMilestoneStepStatus.IN_PROGRESS
                      : CustomerPortalMilestoneStepStatus.PENDING
                  )}
                </StepLineContainer>
                <StepLineLabel>{getLabelStatus(step.type)}</StepLineLabel>
              </StepContainer>
            );
          })}
        </ProgressBar>
      </HeaderStepper>
    </Skeleton>
  );
};

export default StepLineComponent;
